import { useCallback, useEffect, useMemo } from "react";
import {
  useEmailConversations,
  emailConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useEmailConversations";
import { EmailConversationModel } from "@jugl-web/rest-api/integrations/models/EmailConversation";
import { EmailMessageModel } from "@jugl-web/rest-api/integrations/models/EmailMessage";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import {
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { EmailConversationTile } from "../EmailConversationTile";
import { ConversationListEmptyState } from "../ConversationListEmptyState";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";
import { reactivateClosedConversation } from "../../../../utils/reactivateClosedConversation";

export interface EmailConversationsProps {
  searchQuery: string;
  selectedFilter: CRMConversationFilter;
  setSelectedFilter: (value: CRMConversationFilter) => void;
  selectedMode: CRMConversationMode;
}

export const EmailConversations: React.FC<EmailConversationsProps> = ({
  searchQuery,
  selectedFilter,
  setSelectedFilter,
  selectedMode,
}) => {
  const { selectedConversation, selectConversation, incomingEmailMessages$ } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const [fetchConversationById] =
    integrationsApi.useLazyGetEmailConversationByIdQuery();
  const { ref: inViewRef, inView } = useInView();
  const { me } = useMe();

  const computedFilter = useMemo(() => {
    if (selectedFilter !== CRMConversationFilter.active) {
      return selectedFilter;
    }

    switch (selectedMode) {
      case CRMConversationMode.managedByMe:
        return me?.id || "";
      case CRMConversationMode.waitingResponse:
      case CRMConversationMode.onAIAutoReply:
        return selectedMode;
      default:
        return CRMConversationFilter.active;
    }
  }, [selectedFilter, selectedMode, me?.id]);

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    retry,
    loadMore,
  } = useEmailConversations({
    entityId,
    searchTerm: searchQuery,
    filter: computedFilter,
    pageSize: 10,
  });

  useSelectedConversation({
    channel: CRMChannel.email,
    selectedFilter,
    setSelectedFilter,
  });

  const prependConversationById = useCallback(
    async (convId: string) => {
      const response = await fetchConversationById({ entityId, convId });

      if (response && "data" in response && response.data) {
        emailConversations$.next({
          type: "prepend",
          conversation: response.data,
        });
      }
    },
    [entityId, fetchConversationById]
  );

  const mergeMessageIntoConversation = useCallback(
    (
      conversation: EmailConversationModel,
      message: EmailMessageModel & { msg_status?: string }
    ): EmailConversationModel =>
      reactivateClosedConversation({
        ...conversation,
        ...message,
        // transformEmailMessageDtoToModel sets name to conv_id; on conversations
        // name is the thread subject
        name: conversation.name,
      }),
    []
  );

  // Handle real-time message updates
  useEffect(() => {
    const subscription = incomingEmailMessages$.subscribe((message) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        prependConversationById(message.conv_id);
        return;
      }

      // Update selected conversation if it matches
      if (selectedConversation?.conv_id === message.conv_id) {
        if (
          (selectedConversation.msg_id === message.msg_id &&
            (selectedConversation as EmailConversationModel).payload
              .timestamp >= message.payload.timestamp) ||
          selectedConversation.msg_id !== message.msg_id
        ) {
          const updatedConversation = mergeMessageIntoConversation(
            selectedConversation as EmailConversationModel,
            message
          );
          emailConversations$.next({
            type: "update",
            conversation: updatedConversation,
          });
          selectConversation(updatedConversation, true);
        }
      }

      // Find existing conversation and update it
      const existingConversation = conversations.find(
        (c) => c.conv_id === message.conv_id
      );

      if (
        existingConversation &&
        existingConversation.payload.timestamp <= message.payload.timestamp &&
        existingConversation.msg_id !== message.msg_id
      ) {
        const updatedConversation = mergeMessageIntoConversation(
          existingConversation,
          message
        );

        // Prepend conversation to bump it to the top
        emailConversations$.next({
          type: "prepend",
          conversation: updatedConversation,
        });

        // Also update selected conversation if needed
        if (selectedConversation?.conv_id === updatedConversation.conv_id) {
          selectConversation(updatedConversation, true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingEmailMessages$,
    conversations,
    prependConversationById,
    selectedConversation,
    selectConversation,
    mergeMessageIntoConversation,
  ]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <EmailConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      <ConversationListEmptyState
        hasConversations={conversations.length > 0}
        searchQuery={searchQuery}
        selectedFilter={selectedFilter}
        selectedMode={selectedMode}
      />
    </DataLoadingWrapper>
  );
};
